import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import AdminLogin from './components/AdminLogin';
import AdminSidebar from './components/AdminSidebar';
import PermissionGuard from './components/PermissionGuard';
import AdminDashboard from './components/AdminDashboard';
import TargetMembers from './components/TargetMembers';
import AdminMembers from './components/AdminMembers';
import AdminRegistrations from './components/AdminRegistrations';
import RegistrationDetails from './components/RegistrationDetails';
import AdminPayments from './components/AdminPayments';
import AdminInvoices from './components/AdminInvoices';
import AdminTiers from './components/AdminTiers';
import AdminEventFees from './components/AdminEventFees';
import AdminRestrictions from './components/AdminRestrictions';
import AdminUserRoles from './components/AdminUserRoles';
import AdminUsers from './components/AdminUsers';
import EmailTemplates from './components/EmailTemplates';
import AdminSettings from './components/AdminSettings';
import AdminActivityLogs from './components/AdminActivityLogs';
import AdminMemberActivity from './components/AdminMemberActivity';
import AdminReports from './components/AdminReports';
import AdminAnnouncements from './components/AdminAnnouncements';
import AffiliateReferrals from './components/AffiliateReferrals';
import MemberDocumentUpload from './components/MemberDocumentUpload';
import AdminMemberStories from './components/AdminMemberStories';
import AdminLeaderboard from './components/AdminLeaderboard';

// Loading screen
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      <p className="mt-4 text-slate-400">Loading Admin Portal...</p>
    </div>
  </div>
);

// Protected admin layout
const AdminLayout = ({ children }) => (
  <div className="flex h-screen bg-gray-100">
    <AdminSidebar />
    <main className="flex-1 ml-64 overflow-y-auto">
      <div className="p-8">
        {children}
      </div>
    </main>
  </div>
);

// Admin routes component
const AdminRoutes = () => {
  const { isAuthenticated, loading, admin } = useAdminAuth();
  const isAffiliatedMember = admin?.role === 'affiliated_member';
  const homeRoute = isAffiliatedMember ? '/admin/target-members' : '/admin/dashboard';

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={<Navigate to={homeRoute} replace />} />
        <Route path="/dashboard" element={isAffiliatedMember ? <Navigate to="/admin/target-members" replace /> : <PermissionGuard permission={null}><AdminDashboard /></PermissionGuard>} />
        <Route path="/target-members" element={<PermissionGuard permission={['target_members', 'view']}><TargetMembers /></PermissionGuard>} />
        <Route path="/affiliate-referrals" element={<PermissionGuard permission={['affiliate_referrals', 'view']}><AffiliateReferrals /></PermissionGuard>} />
        <Route path="/registrations" element={<PermissionGuard permission={['members', 'validate']}><AdminRegistrations /></PermissionGuard>} />
        <Route path="/registrations/:id" element={<PermissionGuard permission={['members', 'validate']}><RegistrationDetails /></PermissionGuard>} />
        <Route path="/members" element={<PermissionGuard permission={['members', 'view']}><AdminMembers /></PermissionGuard>} />
        <Route path="/members/:memberId/documents" element={<PermissionGuard permission={['members', 'validate']}><MemberDocumentUpload /></PermissionGuard>} />
        <Route path="/members/:memberId/bolt-onboarding" element={<PermissionGuard permission={['members', 'validate']}><MemberDocumentUpload /></PermissionGuard>} />
        <Route path="/payments" element={<PermissionGuard permission={['payments', 'view']}><AdminPayments /></PermissionGuard>} />
        <Route path="/invoices" element={<PermissionGuard permission={['invoices', 'view']}><AdminInvoices /></PermissionGuard>} />
        <Route path="/tiers" element={<PermissionGuard permission={['tiers', 'view']}><AdminTiers /></PermissionGuard>} />
        <Route path="/event-fees" element={<PermissionGuard permission={['event_fees', 'view']}><AdminEventFees /></PermissionGuard>} />
        <Route path="/announcements" element={<PermissionGuard permission={['announcements', 'view']}><AdminAnnouncements /></PermissionGuard>} />
        <Route path="/member-stories" element={<PermissionGuard permission={['announcements', 'view']}><AdminMemberStories /></PermissionGuard>} />
        <Route path="/restrictions" element={<PermissionGuard permission={['members', 'restrict']}><AdminRestrictions /></PermissionGuard>} />
        <Route path="/team" element={<Navigate to="/admin/users" replace />} />
        <Route path="/user-roles" element={<PermissionGuard permission={['roles', 'view']}><AdminUserRoles /></PermissionGuard>} />
        <Route path="/users" element={<PermissionGuard permission={['users', 'view']}><AdminUsers /></PermissionGuard>} />
        <Route path="/templates" element={<PermissionGuard permission={['templates', 'view']}><EmailTemplates /></PermissionGuard>} />
        <Route path="/reports" element={<PermissionGuard permission={['reports', 'view']}><AdminReports /></PermissionGuard>} />
        <Route path="/activity-logs" element={<PermissionGuard permission={['logs', 'view']}><AdminActivityLogs /></PermissionGuard>} />
        <Route path="/member-activity" element={<PermissionGuard permission={['logs', 'view']}><AdminMemberActivity /></PermissionGuard>} />
        <Route path="/leaderboard" element={<PermissionGuard permission={['settings', 'view']}><AdminLeaderboard /></PermissionGuard>} />
        <Route path="/settings" element={<PermissionGuard permission={['settings', 'view']}><AdminSettings /></PermissionGuard>} />
        <Route path="*" element={<Navigate to={homeRoute} replace />} />
      </Routes>
    </AdminLayout>
  );
};

// Main Admin App wrapper
const AdminApp = () => {
  return (
    <AdminAuthProvider>
      <AdminRoutes />
    </AdminAuthProvider>
  );
};

export default AdminApp;
